import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button } from 'react-bootstrap';
import { Plus } from 'lucide-react';
import { PageLoader } from '../../Components/LoadingSpinner';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useParking } from '../../hooks/parking';
import { ParkingAreaInput } from '../../types/parking';
import ParkingAreaModal from './ParkingAreaModal';
import ParkingAreaCard from './ParkingAreaCard';
import './Parking.css';

/** All parking areas of a building: /building/:id/parking */
const ParkingAreasPage: React.FC = () => {
    const { id: buildingKey } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isAdmin = useIsAdmin();
    const { currentBuilding, loadingBuilding, buildingError, fetchBuilding } = useBuilding();
    const { sortedAreas, error, submitting, createArea, createRoom, clearError } = useParking({ buildingKey });
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        if (buildingKey && (!currentBuilding || currentBuilding.key !== buildingKey)) {
            fetchBuilding(buildingKey);
        }
    }, [buildingKey, currentBuilding, fetchBuilding]);

    const buildingName = currentBuilding?.summary?.name || 'Byggnad';
    useDocumentTitle(`Parkering - ${buildingName}`);

    if (loadingBuilding && !currentBuilding) return <PageLoader />;
    if (buildingError) return <p>{buildingError}</p>;
    if (!currentBuilding || !buildingKey) return <p>Byggnaden hittades ej</p>;

    const handleCreate = async (input: ParkingAreaInput) => {
        const area = await createArea(input);
        navigate(`/building/${buildingKey}/parking/${area.key}`);
    };

    return (
        <>
            <BreadcrumbComponent
                items={[
                    { text: buildingName, link: `/building/${buildingKey}` },
                    { text: 'Parkering' },
                ]}
            />

            <div className="parking-page">
                <div className="parking-title-container">
                    <h1 className="parking-page-title page-title">Parkering</h1>
                    {isAdmin && (
                        <div className="parking-title-actions">
                            <Button variant="primary" onClick={() => setShowModal(true)} disabled={submitting}>
                                <Plus size={16} className="me-1" />Nytt parkeringsområde
                            </Button>
                        </div>
                    )}
                </div>

                {error && <Alert variant="danger" dismissible onClose={clearError}>{error}</Alert>}

                <div className="parking-tab-content std-box">
                    {sortedAreas.length === 0 ? (
                        <p className="parking-empty">
                            Ingen parkering registrerad. {isAdmin ? 'Skapa ett parkeringsområde för att börja.' : 'Parkeringsområden registreras av administratörer eller i appen.'}
                        </p>
                    ) : (
                        <div className="parking-building-section">
                            <div className="parking-area-cards">
                                {sortedAreas.map(area => (
                                    <ParkingAreaCard
                                        key={area.key}
                                        area={area}
                                        structure={currentBuilding.model.structure}
                                        to={`/building/${buildingKey}/parking/${area.key}`}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ParkingAreaModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onSubmit={handleCreate}
                structure={currentBuilding.model.structure}
                onCreateRoom={createRoom}
                submitting={submitting}
            />
        </>
    );
};

export default ParkingAreasPage;
